import { AuthorizationWrapper } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { CheckboxInput } from '@frontend/components/basic-forms';
import { Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { UserContactClient } from '@frontend/user/api';
import { CreateUserContact, IUser } from '@frontend/user/types';
import { FormattedMessage } from 'react-intl';

import CreateModal from '../../../modal/create-modal/create-modal.component';
import { FORM_ID, useCreateContactModal } from './create-contact-modal.controller';

export interface CreateContactModalProps {}

export const CreateContactModal = (props: CreateContactModalProps) => {
    const viewProps = useCreateContactModal(props);

    if (viewProps.isLoadingSettings) {
        return (
            <div className='flex h-64 items-center justify-center'>
                <Spinner />
            </div>
        );
    }

    return (
        <CreateModal<IUser, CreateUserContact>
            order={[['first_name', 'last_name'], 'email', 'mobile', 'language']}
            required={viewProps.getRequiredFields()}
            formId={FORM_ID}
            onSubmit={(v) => {
                return viewProps.shouldCreateCorporateContact
                    ? UserContactClient.createCorporateContact(viewProps.currentAccount, v)
                    : UserContactClient.createMyContact(viewProps.currentAccount, v);
            }}
            submitButtonOverride={
                <FormattedMessage
                    id='feature.contacts.create-contact'
                    defaultMessage='Create contact'
                />
            }
            label={
                <FormattedMessage
                    id='feature.contacts.create-contact'
                    defaultMessage='Create contact'
                />
            }
            title={CommonMessage.FORMS.BASIC_INFORMATION}
            type={EntityType.USER}
            successMessageOverwrite={CommonMessage.FORMS.SUCCESSFULLY_CREATED_ENTITY(CommonMessage.OBJECTS.CONTACT.DEFAULT)}
            fieldProps={() =>
                new Map([
                    ['language', { isClearable: true }],
                    ['email', { isValidCallback: (valid: boolean) => viewProps.setIsEmailValid(valid) }],
                    ['mobile', { isValidCallback: (valid: boolean) => viewProps.setIsMobileValid(valid) }]
                ])
            }
            submitDisabled={viewProps.checkSubmitDisabled}
            subForms={
                new Map(
                    viewProps.accountNotificationSettings && [
                        [
                            <FormattedMessage
                                id='create-contact-modal.notification-preferences'
                                defaultMessage='Notification preferences'
                                key='create-contact-modal.notification-preferences'
                            />,
                            {
                                order: ['email_channel', 'mobile_channel', 'push_channel', 'whatsapp_channel'],
                                expectedType: new Map([
                                    ['email_channel', 'boolean'],
                                    ['mobile_channel', 'boolean'],
                                    ['push_channel', 'boolean'],
                                    ['whatsapp_channel', 'boolean']
                                ]),
                                fieldProps: new Map(
                                    Object.entries(viewProps.accountNotificationSettings).map(([key, value]) => [key as any, { disabled: !value }])
                                )
                            }
                        ]
                    ]
                )
            }
            append={
                <AuthorizationWrapper requiredPermissions={[{ item: EntityType.USER, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: '?' }]}>
                    <div className='mt-4'>
                        <CheckboxInput
                            label={
                                <FormattedMessage
                                    id='create-contact-modal.corporate-contact-toggle'
                                    defaultMessage='Make corporate contact. This contact will be visible to all users in your organization.'
                                    key='create-contact-modal.corporate-contact-toggle'
                                />
                            }
                            onChange={(e) => viewProps.setShouldCreateCorporateContact(e)}></CheckboxInput>
                    </div>
                </AuthorizationWrapper>
            }
        />
    );
};
